import { usePostHog } from 'posthog-js/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { Survey } from 'posthog-js';
import type { ReactElement } from 'react';

import { useAddToast, useToasts } from '../../contexts/ToastContext';
import { window } from '../../globals';

import { OnboardingSurveyFullscreen } from './OnboardingSurvey/OnboardingSurveyFullscreen';

type Props = {
  children: ReactElement;
};

/**
 * Single source of truth for routing active PostHog surveys to their
 * appropriate presentation. Subscribes to PostHog once and, on each
 * `getActiveMatchingSurveys` callback:
 *   - Onboarding survey (`window.posthogOnboardingSurveyId`) → fullscreen
 *     takeover via `OnboardingSurveyFullscreen`, replacing children
 *   - Every other active survey → bottom-right popup via
 *     `addToast({ type: 'survey', ... })`, dispatched at most once per
 *     survey id per mount
 *
 * Onboarding takes priority — when it matches in a given tick we short-circuit
 * before dispatching any other toasts, so the popup never appears layered
 * behind the fullscreen takeover.
 *
 * Mounted under `Application` (authenticated routes) so its lifetime aligns
 * with `posthog.identify`.
 */
export const SurveyProvider = ({ children }: Props) => {
  const posthog = usePostHog();
  const addToast = useAddToast();
  const { hideToast } = useToasts();
  const [activeOnboardingSurvey, setActiveOnboardingSurvey] = useState<Survey | null>(null);
  const dispatchedSurveyIdsRef = useRef<Set<string>>(new Set());

  const onboardingSurveyId = window.posthogOnboardingSurveyId;

  const dispatchSurveyToast = useCallback((survey: Survey) => {
    if (dispatchedSurveyIdsRef.current.has(survey.id)) {
      return;
    }
    dispatchedSurveyIdsRef.current.add(survey.id);
    const toastId = addToast({
      type: 'survey',
      survey,
      onSurveySubmit: () => {
        addToast({
          type: 'success',
          content: 'Thank you for your time answering these questions!',
        });
        hideToast(toastId);
      },
    });
  }, [addToast, hideToast]);

  useEffect(() => {
    if (!posthog) {
      return undefined;
    }
    let isCancelled = false;

    const checkForSurveys = () => {
      posthog.getActiveMatchingSurveys((surveys) => {
        if (isCancelled) {
          return;
        }
        const onboardingMatch = onboardingSurveyId
          ? surveys.find((s) => s.id === onboardingSurveyId)
          : undefined;
        if (onboardingMatch) {
          setActiveOnboardingSurvey(onboardingMatch);
          return;
        }
        surveys.forEach((survey) => {
          if (survey.id !== onboardingSurveyId) {
            dispatchSurveyToast(survey);
          }
        });
      }, true);
    };

    checkForSurveys();
    const unsubscribe = posthog.onFeatureFlags(() => checkForSurveys());
    return () => {
      isCancelled = true;
      unsubscribe();
    };
  }, [posthog, onboardingSurveyId, dispatchSurveyToast]);

  if (activeOnboardingSurvey) {
    return (
      <OnboardingSurveyFullscreen
        survey={activeOnboardingSurvey}
        onClose={() => setActiveOnboardingSurvey(null)}
      />
    );
  }
  return children;
};
